import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Shield, CheckCircle, X, RefreshCw, Trash2, Info, ScanSearch, Lock, Undo2 } from "lucide-react";
import { SwitchToggle } from "@/components/toggles";

import { BrushCanvas } from "@/components/media/AnonymizeDialog";
import api from "@/lib/api";

const DEFAULT_PRIVACY = {
  pixelate_face: false,
  blur_face: false,
  remove_tattoos: true,
};

/**
 * WornPhotoUpload — 3-Stufen-Pipeline für Trag-Fotos pro Listing.
 * Wird im STX-Artikel-Wizard genutzt (Owner-Vorgabe 2026-07-09 Iter75).
 * Voraussetzung: `listingId` muss existieren (Listing wurde bereits gespeichert
 * oder befindet sich in Edit-Modus). Bei brandneuen Listings ohne Save muss
 * das Parent-Component ggf. erst speichern und dann die ID reichen.
 */
export function WornPhotoUpload({ listingId, disabled = false, onChange, onEnsureDraft, titleUrl, onSetTitle }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [urls, setUrls] = useState([]);
  const [max, setMax] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [staging, setStaging] = useState(null);
  const [stagingPreview, setStagingPreview] = useState(null);
  const [stagingPrivacy, setStagingPrivacy] = useState(DEFAULT_PRIVACY);
  const [quota, setQuota] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Pinsel/freies Übermalen (Parität zur globalen AnonymizeDialog-Engine).
  const [brushMode, setBrushMode] = useState(false);
  const [brushStrokes, setBrushStrokes] = useState([]);
  const [brushSize, setBrushSize] = useState(0.05);
  const [anonElapsed, setAnonElapsed] = useState(0);
  const anonAbortRef = useRef(false);
  const [uploadPhase, setUploadPhase] = useState(""); // "uploading" | "detecting" | ""

  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      const r = await api.get(`/modul-g/listings/${listingId}/worn-photo`);
      setUrls(r.data.worn_photo_urls || []);
      setMax(r.data.max || 3);
    } catch (e) {
      // Silent — Listing möglicherweise noch nicht persistiert
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [listingId]);
  useEffect(() => { if (onChange) onChange(urls); }, [urls]);

  const refreshStagingPreview = async (stagingId, variant = "anonymized", overrideListingId = null) => {
    const lid = overrideListingId || listingId;
    if (!lid || !stagingId) return;
    try {
      const res = await api.get(`/modul-g/listings/${lid}/worn-photo/staging/${stagingId}/preview`, {
        params: { variant, t: Date.now() },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setStagingPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      if (variant === "anonymized") return refreshStagingPreview(stagingId, "raw", lid);
    }
  };

  const startUpload = async (fileOrBlob) => {
    // Auto-Draft: falls Parent uns die Möglichkeit gibt, im laufenden Wizard
    // einen Draft anzulegen, tun wir das JETZT — der Seller merkt nichts.
    let effectiveId = listingId;
    if (!effectiveId && onEnsureDraft) {
      effectiveId = await onEnsureDraft();
    }
    if (!effectiveId) {
      setError(t("worn_photo.no_listing"));
      return;
    }
    setUploading(true); setError("");
    setUploadPhase("uploading");
    try {
      const fd = new FormData();
      const filename = fileOrBlob.name || "worn-photo.jpg";
      fd.append("file", fileOrBlob, filename);
      // iter172l · Wechsel auf „detecting"-Phase nach kurzer Wartezeit (Body-Detection läuft ~5-15s)
      const phaseTimer = setTimeout(() => setUploadPhase("detecting"), 800);
      const res = await api.post(`/modul-g/listings/${effectiveId}/worn-photo/staging`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      clearTimeout(phaseTimer);
      setStaging(res.data);
      setQuota(res.data.quota || null);
      // iter88.24 (Owner-Bug 1a): Nach Auto-Draft ist `listingId`-Prop noch leer,
      // aber Backend braucht die neue ID sofort. `overrideListingId` sorgt dafür,
      // dass die erste Preview-Abfrage sofort mit der korrekten ID fährt.
      await refreshStagingPreview(res.data.staging_id, "raw", effectiveId);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d?.errors?.join("; ") || d || "Upload fehlgeschlagen");
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setUploading(false);
      setUploadPhase("");
    }
  };

  // iter172l · Zuschnittstool VOR Upload öffnen (Owner-Vorgabe Wizard-Umbau)
  const handleFileSelected = (file) => {
    if (!file) return;
    // KEIN erzwungener 4:5-Crop mehr: Qwen muss dasselbe (volle) Bild bekommen wie
    // im KI-Labor, sonst kippt das Ergebnis bei festem Seed. Anzeige nutzt ohnehin
    // aspect-square + object-cover. Server begrenzt Größe/EXIF beim Upload.
    startUpload(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const applyAnonymization = async () => {
    if (!staging || previewBlocked) return;
    const brushPoints = brushStrokes.flat();
    if (brushMode && brushPoints.length === 0) {
      setError(t("anonymize.err_brush_empty"));
      return;
    }
    setAnonymizing(true); setError(""); setAnonElapsed(0);
    anonAbortRef.current = false;
    const startTs = Date.now();
    const ticker = setInterval(() => setAnonElapsed(Math.floor((Date.now() - startTs) / 1000)), 1000);
    try {
      const payload = { ...stagingPrivacy };
      if (brushPoints.length) { payload.brush_strokes = brushPoints; payload.brush_radius = brushSize; }
      // iter173n · Asynchron: Job starten → Status pollen (kein Proxy-Timeout mehr).
      const { data: started } = await api.post(
        `/modul-g/listings/${listingId}/worn-photo/staging/${staging.staging_id}/anonymize`,
        payload, { timeout: 60000 });
      if (started?.quota) setQuota(started.quota);
      const statusUrl = `/modul-g/listings/${listingId}/worn-photo/staging/${staging.staging_id}/anonymize-status`;
      const deadline = Date.now() + 360000;
      let result = null;
      while (Date.now() < deadline) {
        if (anonAbortRef.current) return;
        await new Promise((r) => setTimeout(r, 2500));
        if (anonAbortRef.current) return;
        let st;
        try { st = (await api.get(statusUrl, { timeout: 30000 })).data; } catch { continue; }
        if (st.state === "DONE") { result = st; break; }
        if (st.state === "FAILED") { throw new Error(st.error || "failed"); }
      }
      if (anonAbortRef.current) return;
      if (!result) throw new Error("timeout");
      setStaging((cur) => ({ ...cur, ...result, status: "ANONYMIZED" }));
      if (result?.quota) setQuota(result.quota);
      if (result?.tattoo_error === "RESOURCES_BUSY") {
        setError(t("anonymize.tattoo_busy"));
      } else if (result?.marks_requested && !result?.marks_applied) {
        setError(t("anonymize.marks_not_applied"));
      }
      if (brushPoints.length) setBrushMode(false);
      await refreshStagingPreview(staging.staging_id, "anonymized");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error_code === "ANON_QUOTA_EXCEEDED") {
        setQuota((q) => ({ ...(q || {}), gated: true, unlimited: false, allowed: false,
          used: d.used, limit: d.limit, plan_code: d.plan_code }));
        setError(d.message || t("anonymize.quota_exhausted_title"));
      } else if (typeof d === "string" || d?.message) {
        setError(d?.message || d);
      } else {
        setError(t("anonymize.err_timeout"));
      }
    } finally {
      clearInterval(ticker);
      setAnonymizing(false);
    }
  };

  const confirmStaging = async () => {
    if (!staging) return;
    setConfirming(true); setError("");
    try {
      const res = await api.post(`/modul-g/listings/${listingId}/worn-photo/staging/${staging.staging_id}/confirm`, { label: null, use_anonymized: staging.status === "ANONYMIZED" });
      if (stagingPreview) URL.revokeObjectURL(stagingPreview);
      setStagingPreview(null);
      setStaging(null);
      setStagingPrivacy(DEFAULT_PRIVACY);
      setBrushMode(false); setBrushStrokes([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
      // iter172f · Body-Content-Hinweis nach Trage-Foto-Confirm
      if (res?.data?.explicit_detected) {
        setError(t("worn_photo.body_content_detected"));
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d || "Bestätigung fehlgeschlagen");
    } finally {
      setConfirming(false);
    }
  };

  const cancelStaging = async () => {
    anonAbortRef.current = true;
    if (!staging) return;
    try { await api.delete(`/modul-g/listings/${listingId}/worn-photo/staging/${staging.staging_id}`); } catch (_e) { /* ignore cancel error */ }
    if (stagingPreview) URL.revokeObjectURL(stagingPreview);
    setStagingPreview(null);
    setStaging(null);
    setStagingPrivacy(DEFAULT_PRIVACY);
    setBrushMode(false); setBrushStrokes([]);
    setAnonymizing(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = async (url) => {
    if (!window.confirm(t("worn_photo.confirm_delete"))) return;
    try {
      await api.delete(`/modul-g/listings/${listingId}/worn-photo`, { params: { url } });
      await load();
    } catch (e) {
      setError(t("worn_photo.delete_failed"));
    }
  };

  const canAdd = urls.length < max && !disabled;
  const previewBlocked = quota && quota.gated && !quota.unlimited && (quota.allowed === false);

  return (
    <div data-testid="worn-photo-section" className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium text-[#666] uppercase tracking-wider">
          Trage-Fotos (anonymisiert) ({urls.length}/{max})
        </p>
      </div>
      <div className="border-l-2 border-emerald-500 bg-emerald-50 p-2 flex gap-1.5">
        <Info size={12} className="text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-[10px] text-emerald-900 leading-snug">
          Foto von dir mit dem Artikel angezogen. Wird automatisch anonymisiert (Gesicht + optional Tattoos) und ist die Basis für die KI-Vorschau der Käufer. <strong>{t("worn_photo.original_deleted")}</strong>
        </p>
      </div>

      {loading && <p className="text-[10px] text-[#999] italic">{t("worn_photo.loading")}</p>}

      {urls.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {urls.map((url, idx) => {
            const isTitle = onSetTitle && titleUrl === url;
            return (
            <div key={url + idx} data-testid={`worn-thumb-${idx}`} className={`relative aspect-square bg-[#F5F5F5] border ${isTitle ? "border-[#0A0A0A]" : "border-emerald-400"} overflow-hidden`}>
              <img src={url} alt={`Trage-Foto ${idx + 1}`} className="w-full h-full object-cover" />
              <span className="absolute top-1 left-1 text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 uppercase tracking-wider">Trage</span>
              {isTitle && (
                <span className="absolute top-1 left-14 text-[9px] bg-[#0A0A0A] text-white px-1.5 py-0.5 uppercase tracking-wider">Titel</span>
              )}
              <div className="absolute top-1 right-1 flex gap-1">
                {onSetTitle && !isTitle && (
                  <button
                    type="button"
                    title="Als Titelbild"
                    data-testid={`worn-title-${idx}`}
                    onClick={() => onSetTitle(url)}
                    className="w-6 h-6 bg-white/90 border border-[#E5E5E5] text-[10px] font-bold text-[#0A0A0A] hover:bg-white">
                    ★
                  </button>
                )}
                <button
                  data-testid={`worn-remove-${idx}`}
                  onClick={() => removePhoto(url)}
                  className="w-6 h-6 bg-white/90 border border-[#E5E5E5] rounded-full flex items-center justify-center hover:bg-red-50">
                  <Trash2 size={11} className="text-red-500" />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {canAdd && (
        <>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="worn-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex-1 h-10 rounded-full border border-emerald-600 text-emerald-700 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
              {uploading ? (
                uploadPhase === "detecting" ? (
                  <><ScanSearch size={12} className="animate-pulse" /> {t("worn_photo.analyzing_body_regions")}</>
                ) : (
                  <><Loader2 size={12} className="animate-spin" /> Wird hochgeladen …</>
                )
              ) : <><Plus size={12} /> Bild hinzufügen</>}
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFileSelected(e.target.files[0])} />
          {/* iter172l · Ladebalken-Overlay während Body-Detection (blockierend, kein Klick) */}
          {uploading && (
            <div data-testid="worn-upload-progress" className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center pointer-events-none">
              <div className="bg-white rounded-2xl px-6 py-5 flex flex-col items-center gap-3 shadow-2xl max-w-xs">
                {uploadPhase === "detecting" ? (
                  <ScanSearch className="w-8 h-8 text-emerald-600 animate-pulse" />
                ) : (
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                )}
                <p className="text-sm font-semibold text-[#0A0A0A]">
                  {uploadPhase === "detecting" ? "Analysiere Körperregionen …" : "Bild wird hochgeladen …"}
                </p>
                <p className="text-[11px] text-[#666] text-center leading-snug">
                  {uploadPhase === "detecting"
                    ? "Unser System erkennt automatisch, welche Bildbereiche später für Nicht-AVS-User geblurrt werden."
                    : "Original wird sicher im verschlüsselten Vault abgelegt."}
                </p>
                <div className="w-full h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                  <div className={`h-full bg-emerald-500 ${uploadPhase === "detecting" ? "w-2/3 animate-pulse" : "w-1/3 animate-pulse"}`} />
                </div>
              </div>
            </div>
          )}
        </>
      )}



      {error && <div data-testid="worn-error" className="border border-red-400 bg-red-50 text-red-700 text-xs p-2">{error}</div>}

      {staging && (
        <div data-testid="worn-staging-dialog" className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center overflow-y-auto">
          <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
            <div className="sticky top-0 bg-white flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <Shield size={14} /> {staging.status === "ANONYMIZED" ? "Anonymisierung prüfen" : "Anonymisierung wählen"}
              </p>
              <button onClick={cancelStaging} className="p-1.5 -mr-1.5" disabled={confirming}>
                <X size={18} />
              </button>
            </div>
            <div className="bg-[#F5F5F5] flex items-center justify-center relative" style={{ minHeight: "40vh" }}>
              {brushMode && stagingPreview ? (
                <BrushCanvas imageUrl={stagingPreview} strokes={brushStrokes} onChange={setBrushStrokes} radiusPct={brushSize} />
              ) : stagingPreview ? (
                <img src={stagingPreview} data-testid="worn-staging-img" alt="Vorschau" className="max-w-full object-contain" style={{ maxHeight: "40vh" }} />
              ) : <Loader2 className="animate-spin text-[#666]" />}
              {anonymizing && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-white border border-[#E5E5E5] px-4 py-3 flex flex-col items-center gap-2 w-60">
                    <div className="flex items-center gap-2">
                      <ScanSearch size={14} className="text-emerald-600 animate-pulse" />
                      <span className="text-xs font-semibold">{t("anonymize.working")}</span>
                    </div>
                    <p className="text-[10px] text-[#666] text-center leading-snug">{t("anonymize.working_hint")}</p>
                    <p data-testid="worn-anon-elapsed" className="text-[11px] text-[#0A0A0A] font-medium">{anonElapsed}s</p>
                  </div>
                </div>
              )}
            </div>

            {brushMode && (
              <div data-testid="worn-brush-toolbar" className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-amber-800 flex-1 min-w-[120px]">{t("anonymize.brush_hint")}</span>
                <div className="flex items-center gap-1" data-testid="worn-brush-size">
                  {[["S", 0.03], ["M", 0.05], ["L", 0.08]].map(([lbl, val]) => (
                    <button
                      key={lbl}
                      data-testid={`worn-brush-size-${lbl}`}
                      onClick={() => setBrushSize(val)}
                      disabled={anonymizing || confirming}
                      className={`h-8 w-8 rounded-full border text-xs disabled:opacity-40 ${brushSize === val ? "bg-amber-500 text-white border-amber-500" : "border-amber-400 text-amber-800"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="worn-brush-undo-btn"
                  onClick={() => setBrushStrokes((s) => s.slice(0, -1))}
                  disabled={anonymizing || confirming || !brushStrokes.length}
                  className="h-8 px-2 rounded-full border border-amber-400 text-amber-800 text-xs flex items-center gap-1 disabled:opacity-40">
                  <Undo2 size={12} /> {t("anonymize.undo")}
                </button>
                <button
                  data-testid="worn-brush-clear-btn"
                  onClick={() => setBrushStrokes([])}
                  disabled={anonymizing || confirming || !brushStrokes.length}
                  className="h-8 px-2 rounded-full border border-amber-400 text-amber-800 text-xs flex items-center gap-1 disabled:opacity-40">
                  <Trash2 size={12} /> {t("anonymize.clear")}
                </button>
              </div>
            )}
            <div className="p-4 space-y-2">
              <p className="text-[11px] text-[#666]">Klick <strong>{t("worn_photo.preview_quote")}</strong>{t("worn_photo.after_quote")}</p>

              <div className="border border-[#E5E5E5] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Gesicht</p>
                <label data-testid="worn-face-toggle" className="flex items-center justify-between py-1 cursor-pointer">
                  <div>
                    <span className="text-sm">Gesicht anonymisieren</span>
                    <p className="text-[10px] text-[#999] mt-0.5">Mosaik + Blur — Gesicht komplett unkenntlich</p>
                  </div>
                  <SwitchToggle
                    checked={stagingPrivacy.pixelate_face}
                    disabled={anonymizing || confirming}
                    onChange={() => setStagingPrivacy((cur) => ({ ...cur, pixelate_face: !cur.pixelate_face, blur_face: false }))}
                    variant="neutral"
                    ariaLabel="Gesicht anonymisieren"
                  />
                </label>
              </div>

              <div className="border border-[#E5E5E5] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("worn_photo.body_markers")}</p>
                {[
                  { key: "remove_tattoos", label: "Tattoos entfernen", hint: t("anonymize.remove_tattoos_hint") },
                ].map((row) => {
                  const on = !!stagingPrivacy[row.key];
                  return (
                    <label key={row.key} data-testid={`worn-toggle-${row.key}`} className="flex items-center justify-between py-1.5 cursor-pointer">
                      <div className="pr-3">
                        <span className="text-sm">{row.label}</span>
                        {row.hint && <p className="text-[10px] text-[#999] mt-0.5 leading-snug">{row.hint}</p>}
                      </div>
                      <SwitchToggle
                        checked={on}
                        disabled={anonymizing || confirming}
                        onChange={() => setStagingPrivacy((cur) => ({ ...cur, [row.key]: !on }))}
                        variant="neutral"
                        ariaLabel={row.label}
                      />
                    </label>
                  );
                })}
                {/* Pinsel/freies Übermalen beliebiger Bereiche (Parität zur globalen Engine) */}
                <label data-testid="worn-toggle-brush" className="flex items-center justify-between py-1.5 cursor-pointer border-t border-[#EEE] mt-1 pt-2">
                  <div>
                    <span className="text-sm">{t("anonymize.brush_label")}</span>
                    <p className="text-[10px] text-[#999] mt-0.5">{t("anonymize.brush_desc")}</p>
                  </div>
                  <SwitchToggle
                    checked={brushMode}
                    disabled={anonymizing || confirming}
                    onChange={() => { setBrushMode((v) => !v); setError(""); }}
                    variant="neutral"
                    ariaLabel={t("anonymize.brush_label")}
                  />
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button data-testid="worn-anonymize-btn" onClick={applyAnonymization} disabled={anonymizing || confirming || previewBlocked || (brushMode && brushStrokes.flat().length === 0)}
                  className="flex-1 h-12 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {anonymizing
                    ? <><Loader2 size={13} className="animate-spin" /> Anonymisiere …</>
                    : previewBlocked
                      ? <><Lock size={13} /> {t("worn_photo.create_preview")}</>
                      : brushMode
                        ? <><RefreshCw size={13} /> {t("worn_photo.create_preview")} ({brushStrokes.length})</>
                        : <><RefreshCw size={13} /> {t("worn_photo.create_preview")}</>}
                </button>
                <button data-testid="worn-confirm-btn" onClick={confirmStaging} disabled={anonymizing || confirming}
                  className="flex-1 h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {confirming ? <><Loader2 size={13} className="animate-spin" /> Speichere …</> : <><CheckCircle size={13} /> {t("worn_photo.apply")}</>}
                </button>
              </div>
              {quota && quota.gated && !quota.unlimited && (
                <p data-testid="worn-quota-info" className="text-[10px] text-[#999] text-center">
                  {t("anonymize.quota_remaining", { remaining: previewBlocked ? 0 : (quota.remaining ?? 0) })}
                </p>
              )}
              {previewBlocked && (
                <div data-testid="worn-quota-booking" className="border border-purple-300 bg-purple-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-purple-900">{t("anonymize.quota_exhausted_title")}</p>
                  <p className="text-[11px] text-purple-800">{t("anonymize.quota_exhausted_desc")}</p>
                  <button
                    data-testid="worn-quota-book-btn"
                    onClick={() => navigate("/studio/helper")}
                    className="h-9 px-3 bg-purple-600 text-white text-xs font-medium hover:bg-purple-500 rounded-full">
                    {t("anonymize.book_now")}
                  </button>
                </div>
              )}
              {staging.status !== "ANONYMIZED" && !previewBlocked && (
                <p className="text-[10px] text-[#999] text-center">{t("anonymize.apply_asis_note")}</p>
              )}
              <button data-testid="worn-cancel-btn" onClick={cancelStaging} disabled={confirming} className="w-full h-10 rounded-full text-[#666] text-xs disabled:opacity-40">
                Abbrechen &amp; verwerfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
